import { useEffect, useState } from "react";
import { ExternalLink, Ruler, Wrench } from "lucide-react";
import { api, assetUrl } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Install = ({ list, testid }) => list?.length ? (
  <ul className="mt-2 space-y-0.5 text-[0.72rem] text-[var(--ink-soft)]" data-testid={testid}>
    {list.map((c, i) => <li key={i} className={c.status === "fail" ? "text-[#B42318]" : ""}>— {c.message}</li>)}
  </ul>
) : null;

const System = ({ x }) => x.components?.length ? (
  <div className="mt-2 text-[0.72rem] text-[var(--ink-soft)]">
    <span className="font-medium">Components:</span> {x.components.join(", ")}
    {x.requiredMeasurements?.length > 0 && <div className="mt-0.5 flex items-center gap-1"><Ruler size={11} /> Needs: {x.requiredMeasurements.join(", ")}</div>}
  </div>
) : null;

const UsedCard = ({ x, i }) => {
  const p = x.product;
  return (
    <article id={`shop-${p.id}`} className="grid scroll-mt-24 grid-cols-[6rem_1fr] gap-4 border-t border-[var(--line)] py-5 sm:grid-cols-[8rem_1fr]" data-testid={`shop-card-${i}`}>
      <div className="aspect-square overflow-hidden rounded-xl bg-white">{p.imageUrl && <img src={assetUrl(p.imageUrl)} alt={p.name} className="h-full w-full object-contain" />}</div>
      <div className="min-w-0">
        <div className="flex flex-wrap gap-1.5 text-[0.6rem] uppercase tracking-[0.08em] text-[var(--stone-dark)]">
          <span className="rounded-full bg-[var(--sand)] px-2 py-0.5">{x.procurementType}</span><span>{p.category}</span>
        </div>
        <div className="mt-1 text-xs text-[var(--ink-soft)]" data-testid={`shop-card-${i}-manufacturer`}>{p.manufacturer}</div>
        <div className="font-display text-lg leading-tight">{p.name}</div>
        <div className="font-mono-ui text-[0.7rem]" data-testid={`shop-card-${i}-sku`}>Model {p.sku}</div>
        <div className="mt-1.5 text-xs" data-testid={`shop-card-${i}-price`}>
          {p.price != null ? <span className="font-medium">Verified {money(p.price)} · checked {fmtDate(p.priceVerifiedAt)} · {p.sourceDomain}</span> : <span className="text-[var(--stone-dark)]">Price unknown</span>}
          <span className="ml-2 text-[var(--stone-dark)]">{p.availability !== "Unknown" ? `Availability: ${p.availability} (verified)` : "Availability unknown"}</span>
        </div>
        {p.keySpecs?.length > 0 && (
          <ul className="mt-1.5 space-y-0.5 text-[0.72rem] text-[var(--ink-soft)]">
            {p.keySpecs.slice(0, 4).map((s) => <li key={s.name}>{s.name}: {s.value} <a href={s.sourceUrl} target="_blank" rel="noreferrer noopener" className="text-[var(--stone-dark)] underline">spec source</a></li>)}
          </ul>
        )}
        <System x={x} />
        <Install list={x.installation} testid={`shop-card-${i}-install`} />
        <div className="mt-1 flex flex-wrap gap-x-4">
          <a href={p.sourceUrl} target="_blank" rel="noreferrer noopener" className="btn-quiet !min-h-[40px] text-xs" data-testid={`shop-card-${i}-source-link`}>{p.sourceDomain} <ExternalLink size={12} /></a>
          {p.manufacturerUrl && p.manufacturerUrl !== p.sourceUrl && <a href={p.manufacturerUrl} target="_blank" rel="noreferrer noopener" className="btn-quiet !min-h-[40px] text-xs" data-testid={`shop-card-${i}-manufacturer-link`}>Manufacturer specs <ExternalLink size={12} /></a>}
        </div>
      </div>
    </article>
  );
};

export const ShopTheVision = ({ visionId, versionId, onChangeProducts, testid = "shop-section" }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/visions/${visionId}/shop`, { params: versionId ? { versionId } : {} }).then((r) => setD(r.data)).catch(() => setD(false)); }, [visionId, versionId]);
  if (!d) return null;
  return (
    <section className="mt-16" id="shop" data-testid={testid}>
      <div className="eyebrow mb-2">Shop the Vision · {d.version.kind}</div>
      <h2 className="font-display text-3xl tracking-tight">What&apos;s in this room</h2>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--charcoal)] px-4 py-3 text-sm text-[var(--bone)]" data-testid="shop-totals">
        <span>{d.totals.verifiedSubtotal > 0 ? `Verified subtotal ${money(d.totals.verifiedSubtotal)}` : "No verified prices yet"} · {d.totals.unknownCount} items with unknown price</span>
        {d.isOwner && onChangeProducts && <button className="text-xs underline underline-offset-4" onClick={onChangeProducts} data-testid="shop-change-products-button">Change products &amp; regenerate</button>}
      </div>
      {d.used.length > 0 && (
        <div className="mt-6">
          <div className="eyebrow mb-1" data-testid="shop-used-label">{d.usedLabel}</div>
          {d.used.map((x, i) => <UsedCard key={x.lineItemId} x={x} i={i} />)}
        </div>
      )}
      {!d.used.length && <p className="mt-6 text-sm text-[var(--stone-dark)]" data-testid="shop-none-used">No verified products were used in this version{d.sourced ? "" : " — products haven't been sourced yet"}.</p>}
      {d.conceptual.length > 0 && (
        <div className="mt-8" data-testid="shop-conceptual">
          <div className="eyebrow mb-2">Conceptual — not sourced</div>
          {d.conceptual.map((x) => (
            <div key={x.lineItemId} className="border-t border-[var(--line)] py-3 text-sm">
              <div className="flex justify-between gap-3"><span>{x.lineItem}</span><span className="text-xs text-[var(--stone-dark)]">{x.procurementType} · {x.note}</span></div>
              <System x={x} />
              <Install list={x.installation} />
            </div>
          ))}
        </div>
      )}
      {d.services.length > 0 && (
        <div className="mt-8" data-testid="shop-services">
          <div className="eyebrow mb-2">Services</div>
          {d.services.map((s) => <div key={s.id} className="flex justify-between gap-3 border-t border-[var(--line)] py-3 text-sm"><span className="flex items-center gap-2"><Wrench size={13} /> {s.name}</span><span className="text-xs text-[var(--stone-dark)]">Quote required</span></div>)}
        </div>
      )}
      <p className="mt-4 text-[0.68rem] text-[var(--stone-dark)]">{d.compatLabel}</p>
    </section>
  );
};
